import React from "react";
import { MotifButton, MotifErrorMessage } from "@ey-xd/motif-react";
import { useTranslation } from "react-i18next";

type SectionErrorBoundaryProps = {
  children?: React.ReactNode;
  /** Localized message shown when the section crashes. */
  title?: string;
  /** Localized label for the retry button. */
  retryLabel?: string;
  /** Optional hook for logging the error to a service. */
  onError?: (error: Error, info: React.ErrorInfo) => void;
};

type SectionErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Catches render errors in a single section (tab, grid, panel...) so a crash
 * there does not tear down the whole app. Shows a compact inline fallback with
 * a Retry button that re-mounts only this subtree.
 */
class Boundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  state: SectionErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  handleRetry = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) {
      return (
        <div className="section-error-boundary" style={{ padding: "1rem" }}>
          <MotifErrorMessage>{this.props.title}</MotifErrorMessage>
          <MotifButton
            size="small"
            variant="secondary"
            type="button"
            style={{ marginTop: "0.75rem" }}
            onClick={this.handleRetry}
          >
            {this.props.retryLabel}
          </MotifButton>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function SectionErrorBoundary(
  props: SectionErrorBoundaryProps
) {
  const { t } = useTranslation();
  return (
    <Boundary
      title={props.title ?? t("global.globalNotifications.wentWrong", { defaultValue: "Something went wrong!" })}
      retryLabel={
        props.retryLabel ??
        t("global.globalActions.tryAgain", { defaultValue: "Try again" })
      }
      onError={props.onError}
    >
      {props.children}
    </Boundary>
  );
}
